import { Component, inject, Input } from '@angular/core';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { IOptionItem, SelectComponent } from '@ui-lib/components';
import { NoopValueAccessorDirective } from './noop-value-accessor.directive';
import { BehaviorSubject, map, Observable, scan, shareReplay, switchMap, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import {
  FacilityDictionaryClient,
  SearchFacilitiesDictionaryRequest,
} from '../../../core/openapi/bff-admin-api';

@Component({
  selector: 'mon-facility-select',
  templateUrl: './facility-select.component.html',
  styleUrl: './facility-select.component.scss',
  imports: [ReactiveFormsModule, SelectComponent, AsyncPipe],
  hostDirectives: [NoopValueAccessorDirective],
})
export class FacilitySelectComponent {
  private readonly facilityDictionaryClient = inject(FacilityDictionaryClient);

  private readonly ngControl = inject(NgControl, { self: true, optional: true });

  @Input()
  public label: string | null = null;

  @Input()
  public labelTranslateKey: string | null = null;

  @Input()
  public fullWidth = false;

  private loadedCount = 0;
  private totalCount = 0;
  private readonly pageSize = 10 as const;

  public get control(): FormControl {
    const control = this.ngControl?.control;

    if (!control) {
      throw new Error('mon-facility-select must be used with formControl or formControlName');
    }

    if (!(control instanceof FormControl)) {
      throw new Error('mon-facility-select supports only FormControl');
    }

    return control;
  }

  private initialRequest: Required<
    Pick<SearchFacilitiesDictionaryRequest, 'skip' | 'take' | 'includeTotalCount'>
  > = {
    skip: 0,
    take: this.pageSize,
    includeTotalCount: true,
  };

  private readonly request$ = new BehaviorSubject(this.initialRequest);

  public readonly facilityOptions$: Observable<IOptionItem[]> = this.request$.pipe(
    switchMap((request) =>
      this.facilityDictionaryClient.v1FacilityDictionarySearchPost(request).pipe(
        tap((res) => {
          if (res.totalCount != null) {
            this.totalCount = res.totalCount;
          }
        }),
        map((response) =>
          (response.items ?? []).map<IOptionItem>((facility) => ({
            id: facility.id,
            label: facility.name,
          })),
        ),
      ),
    ),
    scan((acc: IOptionItem[], batch: IOptionItem[]): IOptionItem[] => {
      if (!batch.length) {
        return acc;
      }

      const seen = new Set(acc.map((x) => x.id));
      const merged = [...acc];

      for (const item of batch) {
        if (!item.id) continue;
        if (seen.has(item.id)) continue;

        seen.add(item.id);
        merged.push(item);
      }

      this.loadedCount = merged.length;

      return merged;
    }, []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public loadMore(): void {
    if (this.loadedCount >= this.totalCount) {
      return;
    }

    const current = this.request$.value;

    this.request$.next({
      ...current,
      skip: current.skip + this.pageSize,
      includeTotalCount: false,
    });
  }
}
